import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MAT_SNACK_BAR_DATA } from '@angular/material/snack-bar';

export interface LoadingToastData {
  message: string;
  type: 'loading' | 'success' | 'error';
  thickness?: 'thin' | 'normal' | 'thick';
  prominence?: 'subtle' | 'normal' | 'prominent';
}

/**
 * Component for displaying loading toasts with spinner
 */
@Component({
    selector: 'app-loading-toast',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    MatProgressSpinnerModule,
    MatIconModule
],
    template: `
    <div class="loading-toast-container" [class]="getContainerClasses()">
      <div class="loading-toast-content">
        <div class="loading-toast-icon" [class]="getIconClasses()">
          @if (data.type === 'loading') {
            <mat-spinner
              [diameter]="getSpinnerSize()"
              [strokeWidth]="getSpinnerStroke()">
            </mat-spinner>
          }
          @if (data.type === 'success') {
            <mat-icon
              class="success-icon">
              check_circle
            </mat-icon>
          }
          @if (data.type === 'error') {
            <mat-icon
              class="error-icon">
              error
            </mat-icon>
          }
        </div>
        <span class="loading-toast-message" [class]="getMessageClasses()">{{ data.message }}</span>
      </div>
      <!-- Enhanced visual indicator for prominent toasts -->
      @if (isProminent()) {
        <div class="prominence-indicator"></div>
      }
    </div>
    `,
    styles: [`
    .loading-toast-container {
      display: flex;
      align-items: center;
      min-width: 250px;
      max-width: 450px;
      position: relative;
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    .loading-toast-content {
      display: flex;
      align-items: center;
      gap: 14px;
      width: 100%;
      padding: 2px 0;
    }

    .loading-toast-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 24px;
      min-height: 24px;
      transition: transform 0.2s ease;
    }

    .loading-toast-message {
      font-size: 14px;
      line-height: 1.4;
      color: inherit;
      word-wrap: break-word;
      flex: 1;
      font-weight: 500;
    }

    .success-icon {
      color: #4caf50;
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    .error-icon {
      color: #f44336;
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    /* Thickness variations */
    .container-thick {
      min-width: 300px;
      max-width: 500px;
    }

    .container-thick .loading-toast-content {
      padding: 4px 0;
      gap: 16px;
    }

    .container-thick .loading-toast-message {
      font-size: 15px;
      font-weight: 600;
    }

    .icon-thick {
      min-width: 28px;
      min-height: 28px;
    }

    .icon-thick .success-icon,
    .icon-thick .error-icon {
      font-size: 28px;
      width: 28px;
      height: 28px;
    }

    .message-thick {
      font-size: 15px;
      font-weight: 600;
      letter-spacing: 0.02em;
    }

    /* Prominence variations */
    .container-prominent {
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      border: 2px solid rgba(255, 255, 255, 0.3);
      backdrop-filter: blur(8px);
    }

    .container-prominent .loading-toast-icon {
      animation: prominentPulse 2s ease-in-out infinite;
    }

    .prominence-indicator {
      position: absolute;
      left: -4px;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 70%;
      background: linear-gradient(45deg, #2196f3, #21cbf3);
      border-radius: 2px;
      animation: prominenceGlow 2s ease-in-out infinite alternate;
    }

    /* Enhanced loading spinner styling */
    ::ng-deep .loading-toast-container mat-spinner circle {
      stroke: currentColor;
      opacity: 0.9;
      stroke-linecap: round;
    }

    ::ng-deep .container-thick mat-spinner circle {
      stroke-width: 4px;
    }

    ::ng-deep .container-prominent mat-spinner circle {
      stroke: url(#spinnerGradient);
      filter: drop-shadow(0 0 4px rgba(33, 150, 243, 0.3));
    }

    /* Animations */
    @keyframes prominentPulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.1);
      }
    }

    @keyframes prominenceGlow {
      0% {
        opacity: 0.6;
        box-shadow: 0 0 8px rgba(33, 150, 243, 0.3);
      }
      100% {
        opacity: 1;
        box-shadow: 0 0 16px rgba(33, 150, 243, 0.6);
      }
    }

    /* Ensure consistent alignment */
    .loading-toast-icon mat-spinner,
    .loading-toast-icon mat-icon {
      margin: 0;
    }

    /* Hover effects for better interactivity */
    .loading-toast-container:hover {
      transform: translateY(-1px);
    }

    .container-prominent:hover {
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
    }

    /* Responsive adjustments */
    @media (max-width: 600px) {
      .loading-toast-container {
        min-width: 200px;
        max-width: 350px;
      }

      .container-thick {
        min-width: 220px;
        max-width: 380px;
      }
    }
  `]
})
export class LoadingToastComponent {
  constructor(@Inject(MAT_SNACK_BAR_DATA) public data: LoadingToastData) {}

  getContainerClasses(): string {
    const classes = [];
    
    if (this.data.thickness === 'thick') {
      classes.push('container-thick');
    }
    
    if (this.isProminent()) {
      classes.push('container-prominent');
    }
    
    return classes.join(' ');
  }

  getIconClasses(): string {
    const classes = [];
    
    if (this.data.thickness === 'thick') {
      classes.push('icon-thick');
    }
    
    return classes.join(' ');
  }

  getMessageClasses(): string {
    const classes = [];
    
    if (this.data.thickness === 'thick') {
      classes.push('message-thick');
    }
    
    return classes.join(' ');
  }

  getSpinnerSize(): number {
    switch (this.data.thickness) {
      case 'thick':
        return 28;
      case 'thin':
        return 18;
      default:
        return 24;
    }
  }

  getSpinnerStroke(): number {
    switch (this.data.thickness) {
      case 'thick':
        return 4;
      case 'thin':
        return 2;
      default:
        return 3;
    }
  }

  isProminent(): boolean {
    return this.data.prominence === 'prominent';
  }
} 